import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Badge, ErrorBox, KV, Loading, Panel } from '../components/ui'
import { api } from '../lib/api'
import { longDate } from '../lib/format'
import type { EnquiryDetail as Detail } from '../types'

const STATUSES = ['new', 'replied', 'quoted', 'won', 'lost', 'archived'] as const

export function EnquiryDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const [sender, setSender] = useState('Luis')

  const { data, isLoading, error } = useQuery({
    queryKey: ['enquiry', id],
    queryFn: () => api.get<Detail>(`/enquiries/${id}`),
  })

  // Open with something worth sending, never a blank box. A Claude draft wins if there is
  // one; otherwise the first suggestion, which is stronger than the generic fallback.
  // Never clobber something already being typed.
  useEffect(() => {
    if (body || !data) return
    const best =
      data.draft_source === 'claude' ? data.draft : (data.suggestions[0]?.body ?? data.draft)
    if (best) setBody(best)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.enquiry.id, data?.draft_source])

  const reply = useMutation({
    mutationFn: () => api.post<Detail>(`/enquiries/${id}/reply`, { body, sender_name: sender }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['enquiry', id], fresh)
      queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      queryClient.invalidateQueries({ queryKey: ['overview'] })
      setBody('')
    },
  })

  const simulateReply = useMutation({
    mutationFn: () => api.post<Detail>(`/simulate/reply/${id}`),
    onSuccess: (fresh) => queryClient.setQueryData(['enquiry', id], fresh),
  })

  const deleteEnquiry = useMutation({
    mutationFn: () => api.del(`/enquiries/${id}`),
    onSuccess: () => navigate('/enquiries', { replace: true }),
  })

  const setStatus = useMutation({
    mutationFn: (status: string) => api.post(`/enquiries/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enquiry', id] })
      queryClient.invalidateQueries({ queryKey: ['enquiries'] })
    },
  })

  if (isLoading) return <><PageBar title="Enquiry" /><Content><Loading /></Content></>
  if (error || !data) return <><PageBar title="Enquiry" /><Content><ErrorBox error={error} /></Content></>

  const { enquiry: e, thread } = data
  const hasThread = thread.length > 0

  return (
    <>
      <PageBar
        title={e.name || e.email}
        actions={
          <>
            <Link className="btn ghost" to="/enquiries">Back</Link>
            <Link className="btn" to={`/bookings/new?enquiry_id=${e.id}`}>Create booking</Link>
          </>
        }
      />
      <Content>
        <div className="cols wide-left">
          <section>
            <Panel>
              <div className="detail-head">
                <div className="meta">
                  <span><b>{e.email}</b></span>
                  {e.phone && <span>{e.phone}</span>}
                  <span>{longDate(e.received_at)}</span>
                  <Badge kind="b" value={e.category} />
                  {e.segment !== 'unknown' && <Badge kind="seg" value={e.segment} />}
                  {e.confidence > 0 && (
                    <span className="muted">{Math.round(e.confidence * 100)}% confident</span>
                  )}
                  <span className="muted">via {e.source}</span>
                </div>
              </div>
              <div className="body-text">{e.message}</div>
            </Panel>

            {thread.map((m) => (
              <Panel key={m.id} className={`msg ${m.direction}`}>
                <div className="msg-head">
                  <b>
                    {m.direction === 'outbound'
                      ? `Sent by ${m.sender}`
                      : `From ${e.name || e.email}`}
                  </b>
                  <span className="muted">{new Date(m.sent_at).toLocaleString('en-GB')}</span>
                  {m.automated && <Badge kind="b" value="test" />}
                </div>
                <div className="body-text">{m.body}</div>
              </Panel>
            ))}

            {data.suggestions.length > 0 && (
              <div className="suggest">
                <span className="suggest-label">Suggested replies</span>
                <div className="suggest-chips">
                  {data.suggestions.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      className={`chip-btn t-${s.tone}`}
                      onClick={() => setBody(s.body)}
                      title="Click to load this reply — you can edit it before sending"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Panel className="reply">
              <div className="reply-head">
                <b>Reply</b>
                {hasThread ? (
                  <span className="muted">follow-up</span>
                ) : data.draft_source === 'claude' ? (
                  <span className="muted">drafted by Claude · check before sending</span>
                ) : (
                  <span className="muted">
                    suggestion loaded · edit and send, or pick another above
                  </span>
                )}
              </div>
              <textarea
                rows={hasThread ? 7 : 14}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder={hasThread ? 'Write a follow-up…' : ''}
              />
              <div className="reply-foot">
                <input
                  type="text"
                  value={sender}
                  onChange={(event) => setSender(event.target.value)}
                  aria-label="Sent by"
                />
                <button
                  className="btn"
                  type="button"
                  disabled={!body.trim() || reply.isPending}
                  onClick={() => reply.mutate()}
                >
                  {reply.isPending ? 'Sending…' : 'Send reply'}
                </button>
                {reply.error && <span className="v-alert">Failed to send</span>}
                <button
                  className="btn ghost small"
                  type="button"
                  style={{ marginLeft: 'auto' }}
                  disabled={simulateReply.isPending}
                  onClick={() => simulateReply.mutate()}
                  title="Pretend the client wrote back — for testing the thread without a live mailbox"
                >
                  Simulate client reply
                </button>
              </div>
            </Panel>
          </section>

          <aside>
            <h2 className="sec">Status</h2>
            <Panel pad>
              <KV label="Status">{e.status}</KV>
              <KV label="First reply">
                {e.first_reply_at
                  ? new Date(e.first_reply_at).toLocaleString('en-GB')
                  : <span className="v-alert">not yet</span>}
              </KV>
              <KV label="Handled by">{e.handled_by ?? '—'}</KV>
              {e.booking_id && (
                <KV label="Booking"><Link to={`/bookings/${e.booking_id}`}>#{e.booking_id}</Link></KV>
              )}
              <button
                type="button"
                className="tab danger"
                disabled={deleteEnquiry.isPending}
                onClick={() => {
                  // Permanent, and these are real people: a GDPR erasure request is
                  // the reason this exists, not tidying up.
                  const who = e.name || e.email || 'this enquiry'
                  if (window.confirm(`Permanently delete ${who} and every message?\n\nThis cannot be undone.`)) {
                    deleteEnquiry.mutate()
                  }
                }}
              >
                Delete permanently
              </button>
              <div className="statusform">
                <select
                  value={e.status}
                  onChange={(event) => setStatus.mutate(event.target.value)}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </Panel>
            <p className="hint">
              Sending from here is what makes reply time measurable. A reply sent from Gmail won&rsquo;t
              be seen until mailbox reconciliation lands.
            </p>
          </aside>
        </div>
      </Content>
    </>
  )
}
